import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TriangleAlert } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import {
  PCMP_ACCOUNTS_ACTION_COPY,
  PCMP_FAILURE_STAGE_LABELS,
} from '../../constants/pcmpAccounts.constants';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useFailPcmpProposalAccounts } from '../../hooks/useFailPcmpProposalAccounts';
import type { PcmpFailureStage } from '../../types/pcmpProposal.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const reasonFormSchema = z.object({
  reason: z.string().trim().min(1, 'A reason is required.').max(PCMP_MKT_COMMENT_MAX),
});

type ReasonFormValues = z.infer<typeof reasonFormSchema>;

export interface PcmpAccountsFailTarget {
  proposalId: number;
  refNo: string;
  /** The stage the failure is recorded at; the API derives the same. */
  failureStage?: PcmpFailureStage | null | undefined;
}

interface PcmpAccountsFailDialogProps {
  target: PcmpAccountsFailTarget;
  onClose: () => void;
}

/**
 * Legacy AccountsMarkDisbursementFailed / AccountsPostDisbursementFailed for
 * one proposal. A reason is always required.
 */
export function PcmpAccountsFailDialog({ target, onClose }: PcmpAccountsFailDialogProps) {
  const copy = PCMP_ACCOUNTS_ACTION_COPY.FAIL;
  const form = useForm<ReasonFormValues>({
    resolver: zodResolver(reasonFormSchema),
    defaultValues: { reason: '' },
  });
  const { mutate, isPending, error } = useFailPcmpProposalAccounts(target.proposalId, onClose);

  const onSubmit = form.handleSubmit((values) => mutate({ reason: values.reason }));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={copy.title}
        description={target.refNo}
        icon={TriangleAlert}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={copy.submit}
        submitVariant="destructive"
        pendingLabel={copy.pending}
      >
        <p className="text-sm text-muted-foreground">
          {target.failureStage === 'POST_PAYOUT'
            ? `${PCMP_FAILURE_STAGE_LABELS.POST_PAYOUT}: the payment is reversed and the teacher's due reopens. The proposal goes back to the chain for correction.`
            : `${PCMP_FAILURE_STAGE_LABELS.PRE_PAYOUT}: nothing is paid. The proposal goes back to the chain for correction.`}
        </p>
        <PcmpCommentField
          control={form.control}
          name="reason"
          label="Reason"
          required
          placeholder="Why the payment failed"
          description="Recorded on the proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
